import React, { useState, useEffect } from 'react'
import { MaxHitChart } from './MaxHitChart'
import { Services } from '@/services/services'
import { CurrentSession } from '@/services/repository/session'
import { useTranslations } from '@/hooks/useTranslations'
import { SearchCriteria, SearchCriteriaParams } from '@/pages/communal/statistics/components/common/searchCriteria'
import { mapBuckets } from '../common/labels'
import { HelpPopupButton } from '@/popups/helppopup/help'
import { formatDate } from '@/common/time'
import { MachineType } from '@/services/model/machine'
import { StaticPaginatedTable, StaticPaginatedTableState } from '@/components/search/StaticPaginatedTable'
import { MachineName } from '@/components/machine'
import { HitText } from '@/components/hit/HitText'
import { UserLink } from '@/components/links/user'
import { Order, OrderManager } from '@/components/order/Order'

type MaxHitPopupProps = {
  services: Services
  session: CurrentSession
  criteria: SearchCriteria
  bucketFor: Date
  goToUserPage: (userId: string) => void
  goToMachinePage: (machineId: string) => void
  onClose: () => void
}

interface Machine {
  id: string
  imei: string
  internalNumber: string
  type: MachineType
}

interface User {
  id: string
  username: string
}

interface MachineHit {
  imei: string
  time: string
  hitX: number
  hitY: number
  hitZ: number
  maxHit: number
  machine: Machine
  user: User
  ucid: string
}

const MaxHitPopupOrderCols = ['machine.internalNumber', 'time', 'maxHit', 'user.username']
const MaxHitPopupDefaultOrder: Order = {
  key: 'maxHit',
  asc: false
}

export const MaxHitPopupBody = ({ services, session, criteria, bucketFor, goToUserPage, goToMachinePage, onClose }: MaxHitPopupProps) => {
  const translates = useTranslations(services, session)
  const [response, setResponse] = useState<any | undefined>()
  const [listState, setListState] = useState<StaticPaginatedTableState>({
    pageNumber: 1,
    pageSize: 10
  })
  const [orderManager, setOrderManager] = useState<OrderManager>(() => new OrderManager(
    MaxHitPopupOrderCols,
    MaxHitPopupDefaultOrder,
    (order: Order) => {
      setOrderManager((current) => current.newManager(order))
      setListState((current) => ({ ...current, pageNumber: 1 }))
    }
  ))

  useEffect(() => {
    setResponse(undefined)
    services.session
      .fetchWithToken(`/api/communal/statistics/machines/bucket/hits?bucketFor=${bucketFor.toISOString()}&${SearchCriteriaParams(criteria)}`)
      .then((r) => r.json())
      .then((data) => {
        setResponse(data)
        setListState({
          pageNumber: 1,
          pageSize: 10
        })
      })
  }, [services.session, criteria])

  if (translates === undefined || !response) {
    return <div className='spinner'></div>
  }

  const headerRow = (key: string, translationKey: string) => <div className='hcol' onClick={() => orderManager.click(key)}>
    {translates.for(translationKey)}
    {orderManager.orderInfoElement(key)}
  </div>

  const orderedResponse = orderManager.orderListByKey<MachineHit>(response)

  return (
    <div>
      <h3>{formatDate(new Date(bucketFor))}</h3>

      <StaticPaginatedTable<MachineHit>
        data={orderedResponse}
        parentState={listState}
        setParentState={setListState}
        header={() => <>
          {headerRow('machine.internalNumber', 'elements.table.maxhitpop.machine')}
          {headerRow('time', 'elements.table.maxhitpop.time')}
          {headerRow('maxHit', 'elements.table.maxhitpop.max')}
          {headerRow('user.username', 'elements.table.maxhitpop.user')}
        </>}
        row={(row) => {
          return <>
            <div className={`col`} onClick={() => {
              goToMachinePage(row.machine.id)
              onClose()
            }}>{row.machine ? <MachineName row={row.machine} /> : '-'}</div>
            <div className={`col`}>{row.time ? formatDate(new Date(row.time)) : '-'}</div>
            <div className={`col`}>
              <HitText value={row.maxHit}>{row.maxHit.toFixed(2)}</HitText>
            </div>
            <div className={`col`}>
              <UserLink ucid={row.ucid} user={row.user?.id ? row.user : undefined} />
            </div>
          </>
        }}
        translates={session.translates}
      />
    </div>
  )
}
